import { FC, useMemo } from 'react';

import {
    Box,
    Button,
    CloseButton,
    Drawer,
    Portal,
    Separator,
    Text,
    useSlotRecipe,
} from '@chakra-ui/react';

import { differenceInDays } from 'date-fns/differenceInDays';
import isNil from 'lodash/isNil';

import { dateHelpers, FeatureToggleEnum } from '@nocowanie/core';
import { IconsLinear } from '@nocowanie/icons';

import { RangePickerDrawerProps } from './range-picker-drawer.props';

import { getFeatureToggleValue, wordPluralisation } from '../../../helpers';

export const RangePickerDrawer = ({
    translationData,
    onOpenChange,
    onSubmit,
    open = true,
    startDate,
    endDate,
    displayedDateFormat = 'dd MMM.',
    contentRef,
    footerContent,
    setIsLoading,
    isLoading,
    children,
}: RangePickerDrawerProps) => {
    const FT_USE_NEW_CALENDAR = getFeatureToggleValue<boolean>(FeatureToggleEnum.USE_NEW_CALENDAR);

    const DisplayedValue: FC = () => {
        const nightsLabel = useMemo(() => {
            if (!startDate || !endDate) {
                return;
            }

            const nightsCount = differenceInDays(endDate, startDate);

            return `${nightsCount} ${wordPluralisation(
                Number(nightsCount),
                translationData.nights.singular,
                translationData.nights.plural,
                translationData.nights.genitivePlural,
            )}`;
        }, [startDate, endDate]);

        const getLabelValue = (date: Date | null | undefined, placeholderExtension: string) =>
            date ? (
                <strong>{dateHelpers.format(date, displayedDateFormat)}</strong>
            ) : (
                <>
                    {translationData.datePlaceholder} <strong>{placeholderExtension}</strong>
                </>
            );

        const dateFrom = getLabelValue(startDate, translationData.dateFromPlaceholder);

        if (isNil(startDate) && isNil(endDate)) return dateFrom;

        const dateTo = getLabelValue(endDate, translationData.dateToPlaceholder);

        return (
            <>
                {dateFrom} - {dateTo} {nightsLabel ? `(${nightsLabel})` : null}
            </>
        );
    };

    const renderWeekDayNames = useMemo(() => {
        const [first, ...rest] = [...translationData.weekDayNames];
        const weekDayNames: Array<string> = [...rest, first];

        return weekDayNames?.map(day => (
            <Text as="span" key={day} className="react-datepicker__day-name">
                {day}
            </Text>
        ));
    }, [translationData]);

    const recipe = useSlotRecipe({
        key: 'rangePickerDrawer',
    });
    const styles = recipe();

    const handleSubmit = () => {
        if (!startDate || !endDate) {
            return;
        }

        setIsLoading?.(true);
        'requestIdleCallback' in window ? requestIdleCallback(() => onSubmit?.()) : onSubmit?.();
    };

    return (
        <Drawer.Root open={open} placement="bottom" onOpenChange={onOpenChange} variant={'overlap'}>
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content pt={8} className={'wc-range-picker-drawer'}>
                        <Drawer.CloseTrigger asChild top={0.5} right={0.5}>
                            <CloseButton>
                                <IconsLinear.Close />
                            </CloseButton>
                        </Drawer.CloseTrigger>
                        {renderWeekDayNames ? (
                            <Drawer.Header
                                fontSize="md"
                                borderBottomWidth={1}
                                flexDirection={'column'}
                                gap={0}
                                pb={2}
                                mb={2}
                            >
                                <Separator
                                    width={'100vw'}
                                    borderColor={'{colors.border}'}
                                    opacity={1}
                                />
                                <Box className="react-datepicker--drawer" p={'0 0.5rem'}>
                                    <Box className="react-datepicker__day-names">
                                        {renderWeekDayNames}
                                    </Box>
                                </Box>
                            </Drawer.Header>
                        ) : null}
                        <Drawer.Body
                            pt={0}
                            pb={3}
                            px={6}
                            display={'flex'}
                            flexDirection={'column'}
                            ref={contentRef}
                            overflow={FT_USE_NEW_CALENDAR ? 'hidden' : 'auto'}
                        >
                            {children}
                        </Drawer.Body>
                        <Drawer.Footer
                            flexDirection={'column'}
                            gap={4}
                            borderTopWidth={1}
                            mt={0}
                            pt={3}
                            css={styles.footerContent}
                        >
                            <Box>{footerContent ?? <DisplayedValue />}</Box>
                            <Button
                                css={styles.button}
                                variant={'solid'}
                                onClick={handleSubmit}
                                loading={isLoading && !!(startDate && endDate)}
                                disabled={!!(startDate && !endDate)}
                            >
                                {translationData.submitAction}
                            </Button>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};
